<template>
  <div class="prompt-inline">
    <div class="prompt-inline__header">
      <PromptModeSwitch v-model="currentMode" :test-id-suffix="testIdSuffix" />
      <div class="prompt-inline__actions">
        <span
          v-if="maxlength"
          class="prompt-inline__counter"
          :data-testid="`${testIdSuffix}-counter`"
        >
          {{ length }}/{{ maxlength }}
        </span>
        <slot name="actions" />
        <BotIcon
          v-if="canFullscreen"
          name="fullscreen"
          size="16"
          padding="4"
          class="prompt-inline__expand"
          :data-testid="`open-fullscreen-${testIdSuffix}-button`"
          :tip="
            $t(
              'components.PromptEditor.PromptInputWrapper.308759c198e2409c2b9c2813a126896c'
            )
          "
          @click="handleOpenFullscreen"
        />
      </div>
    </div>

    <div class="prompt-inline__body" :style="{ height: bodyHeight }">
      <template v-if="currentMode === 'edit'">
        <VariableMentionEditor
          v-if="type === 'rich'"
          ref="controlRef"
          v-model="innerValue"
          class="prompt-inline__control"
          height="100%"
          v-bind="controlBindings"
        />
        <u-input
          v-else
          ref="controlRef"
          v-model="innerValue"
          class="prompt-inline__control"
          type="textarea"
          v-bind="controlBindings"
        />
      </template>

      <!-- Preview only renders the current text; the stored prompt is untouched. -->
      <div
        v-else
        class="prompt-inline__preview markdown-body"
        :data-testid="`${testIdSuffix}-preview-content`"
        v-html="renderedMarkdown"
      />
    </div>

    <FullscreenPromptEditor
      v-if="canFullscreen"
      v-model:visible="fullscreenVisible"
      :title="title"
      :value="innerValue"
      @cancel="handleFullscreenCancel"
    >
      <div class="prompt-inline__fullscreen-control">
        <VariableMentionEditor
          v-if="type === 'rich'"
          v-model="innerValue"
          height="100%"
          v-bind="fullscreenProps"
        />
        <u-input
          v-else
          v-model="innerValue"
          type="textarea"
          v-bind="fullscreenProps"
        />
      </div>
    </FullscreenPromptEditor>
  </div>
</template>

<script>
import { computed, defineComponent, inject, ref } from 'vue';
import VariableMentionEditor from '@C/VariableMentionEditor/index.vue';
import { EDIT_DIALOG_INJECT_KEY } from '@C/EditDialog.vue';
import md from '@/libs/markdown';
import { toPreviewMarkdown } from '@C/PromptEditor/promptPreview';
import FullscreenPromptEditor from './FullscreenPromptEditor.vue';
import PromptModeSwitch from './PromptModeSwitch.vue';

// Sizing props are dropped: the card body controls the height instead.
const LAYOUT_ONLY_PROPS = [
  'height',
  'minHeight',
  'maxHeight',
  'showLimit',
  'rows',
  'autosize'
];

// The fullscreen copy should fill the modal body instead of keeping the
// caller's inline sizing classes.
const FULLSCREEN_STRIP_PROPS = [
  ...LAYOUT_ONLY_PROPS,
  'style',
  'class',
  'autofocus'
];

// Editor methods callers rely on (refs to the inline control today).
const FORWARDED_METHODS = [
  'insertMentionByOption',
  'insertVariable',
  'setFocus',
  'focus',
  'blur',
  'clear',
  'getText'
];

/**
 * Prompt editor for fields that already live inside a dialog: it keeps the
 * 编辑 / 预览 switch and the character counter inline, and offers a fullscreen
 * editor that fills the viewport so it is clearly distinct from the dialog.
 */
export default defineComponent({
  name: 'PromptInlineEditor',
  components: { FullscreenPromptEditor, PromptModeSwitch, VariableMentionEditor },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    // rich: 统一提示词编辑器（支持 @ / & 触发候选）, textarea: 普通文本域
    type: {
      type: String,
      default: 'rich',
      validator: value => ['rich', 'textarea'].includes(value)
    },
    testIdSuffix: {
      type: String,
      default: 'prompt-inline'
    },
    title: {
      type: String,
      default: ''
    },
    showFullscreen: {
      type: Boolean,
      default: true
    },
    // Optional controlled mode ('edit' | 'preview'); omit for internal state.
    mode: {
      type: String,
      default: '',
      validator: value => ['', 'edit', 'preview'].includes(value)
    },
    // Shows the counter in the card header; also enforces the limit.
    maxlength: {
      type: Number,
      default: 0
    },
    height: {
      type: [Number, String],
      default: 240
    },
    controlProps: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue', 'update:mode'],
  setup(props, { emit, expose }) {
    const innerMode = ref('edit');
    const controlRef = ref(null);
    const fullscreenVisible = ref(false);
    // Dialog prompt cards get the fullscreen entry; page-level prompts keep
    // using PromptInputWrapper so their 90% modal behaviour is untouched.
    const inDialog = inject(EDIT_DIALOG_INJECT_KEY, false);

    const currentMode = computed({
      get: () => props.mode || innerMode.value,
      set: value => {
        innerMode.value = value;
        emit('update:mode', value);
      }
    });

    const innerValue = computed({
      get: () => props.modelValue,
      set: value => emit('update:modelValue', value)
    });

    // The card never grows with the content: it keeps a fixed height and the
    // editor / preview scroll inside. min() keeps it inside short viewports.
    const bodyHeight = computed(() =>
      typeof props.height === 'number'
        ? `min(${props.height}px, 55vh)`
        : props.height
    );

    // u-input declares the prop as `maxlength`, while the rich editor uses
    // `maxLength`; map the card-level limit accordingly.
    const applyMaxLength = rest => {
      if (!props.maxlength) return rest;
      if (props.type === 'rich') {
        rest.maxLength = props.maxlength;
      } else {
        rest.maxlength = props.maxlength;
      }
      return rest;
    };

    const controlBindings = computed(() => {
      const rest = { ...props.controlProps };
      LAYOUT_ONLY_PROPS.forEach(key => delete rest[key]);
      return applyMaxLength(rest);
    });

    const fullscreenProps = computed(() => {
      const rest = { ...props.controlProps };
      FULLSCREEN_STRIP_PROPS.forEach(key => delete rest[key]);
      return applyMaxLength(rest);
    });

    // A read-only card has nothing to edit, so hide the fullscreen entry.
    const canFullscreen = computed(
      () => inDialog && props.showFullscreen && !props.controlProps?.disabled
    );

    const handleOpenFullscreen = () => {
      fullscreenVisible.value = true;
    };

    // 取消 in the modal restores the content captured when it was opened.
    const handleFullscreenCancel = value => {
      innerValue.value = typeof value === 'string' ? value : '';
    };

    const length = computed(() => [...String(props.modelValue || '')].length);

    // Normalize rich-editor HTML back to Markdown source before rendering, and
    // skip asterisk masking so `**bold**` beside CJK characters renders.
    const renderedMarkdown = computed(() =>
      md.render(toPreviewMarkdown(props.modelValue), { maskAsterisks: false })
    );

    const forwarded = {};
    FORWARDED_METHODS.forEach(name => {
      forwarded[name] = (...args) => controlRef.value?.[name]?.(...args);
    });
    expose(forwarded);

    return {
      bodyHeight,
      canFullscreen,
      controlBindings,
      controlRef,
      currentMode,
      fullscreenProps,
      fullscreenVisible,
      handleFullscreenCancel,
      handleOpenFullscreen,
      innerValue,
      length,
      renderedMarkdown
    };
  }
});
</script>

<style lang="less" scoped>
@import '@/styles/mixins.less';

.prompt-inline {
  padding: 12px 16px;
  border: 1px solid var(--border-neutral-color-secondary);
  border-radius: var(--corner-radius-border-radius);
  background: var(--background-module-bg-color-gray-light);
}

.prompt-inline__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.prompt-inline__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.prompt-inline__counter {
  color: var(--text-neutral-color-text-tertiary);
  font-size: 12px;
  line-height: 20px;
}

.prompt-inline__expand {
  flex: none;
  cursor: pointer;
  color: var(--text-neutral-color-text-tertiary);

  &:hover {
    color: var(--text-primary-color-primary-text);
  }
}

.prompt-inline__body {
  display: flex;
  flex-direction: column;
  // Never collapse (e.g. a caller passing an unusable height).
  min-height: 80px;
  overflow: hidden;
  background: var(--background-neutral-bg-color-container);
  border-radius: var(--corner-radius-border-radius-sm);
}

.prompt-inline__control {
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;

  // uui renders textarea as .uui-input-wrap > textarea.
  :deep(.uui-input-wrap-textarea) {
    display: flex;
    flex-direction: column;
    height: 100%;
    box-sizing: border-box;
  }

  :deep(textarea),
  :deep(.ProseMirror),
  :deep(.tiptap) {
    .prompt-thin-scrollbar();
  }

  :deep(textarea) {
    flex: 1;
    min-height: 0;
    // uui may leave an inline height from autosize; clamp it to the card body
    // so long text scrolls inside instead of being clipped.
    max-height: 100%;
    height: 100%;
    // uui leaves inline overflow/height behind when autosize is used, so the
    // clamp + scroll must win over it.
    overflow-y: auto !important;
    overscroll-behavior: contain;
    resize: none;
  }

  // Rich editors scroll their own content, never the dialog around them.
  :deep(.ProseMirror),
  :deep(.tiptap) {
    overscroll-behavior: contain;
  }
}

.prompt-inline__preview {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px 12px;
  border: 1px solid var(--border-neutral-color-secondary);
  border-radius: var(--corner-radius-border-radius-sm);
  box-sizing: border-box;
  .prompt-thin-scrollbar();
}

// The editor fills the fullscreen modal body, so the plain textarea needs to
// fill and scroll instead of keeping the inline card height.
.prompt-inline__fullscreen-control {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  // Rich editor: keep a definite height so .tiptap can scroll internally.
  :deep(.variable-mention-editor),
  :deep(.editor-shell) {
    height: 100%;
    min-height: 0;
  }

  // uui renders textarea as .uui-input-wrap > textarea (+ word counter).
  :deep(.uui-input-wrap-textarea) {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  :deep(textarea),
  :deep(.ProseMirror),
  :deep(.tiptap) {
    .prompt-thin-scrollbar();
  }

  :deep(textarea) {
    flex: 1;
    min-height: 0;
    height: auto;
    max-height: 100%;
    // uui leaves inline overflow/height behind when autosize is used, so the
    // clamp + scroll must win over it.
    overflow-y: auto !important;
    overscroll-behavior: contain;
    resize: none;
  }

  :deep(.ProseMirror),
  :deep(.tiptap) {
    overscroll-behavior: contain;
  }
}
</style>
